import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmergencyProcedures = () => {
  const [activeScenario, setActiveScenario] = useState('fire');

  const scenarios = {
    fire: {
      title: "Electrical Fire",
      icon: "Flame",
      color: "var(--color-error)",
      steps: [
        {
          step: 1,
          title: "Do NOT Use Water",
          description: "Never use water on an electrical fire. Water conducts electricity and can cause electrocution or spread the fire.",
          icon: "Droplet",
          critical: true
        },
        {
          step: 2,
          title: "Cut the Power",
          description: "If safe to do so, turn off power at the main circuit breaker. Do not approach if flames are near the electrical panel.",
          icon: "Power",
          critical: true
        },
        {
          step: 3,
          title: "Use Correct Extinguisher",
          description: "Use a Class C fire extinguisher designed for electrical fires. Aim at the base of the fire and sweep side to side.",
          icon: "Wind",
          critical: false
        },
        {
          step: 4,
          title: "Evacuate if Necessary",
          description: "If the fire is spreading or you cannot safely extinguish it, evacuate immediately and call emergency services.",
          icon: "DoorOpen",
          critical: true
        },
        {
          step: 5,
          title: "Call Professional Help",
          description: "After the fire is out, contact Gert at 082 793 5785 to inspect and repair electrical damage before restoring power.",
          icon: "Phone",
          critical: false
        }
      ]
    },
    shock: {
      title: "Electrical Shock",
      icon: "Zap",
      color: "var(--color-warning)",
      steps: [
        {
          step: 1,
          title: "Do NOT Touch the Person",
          description: "If someone is being shocked, do not touch them directly. You could become a victim too. Electricity can pass through the body.",
          icon: "Hand",
          critical: true
        },
        {
          step: 2,
          title: "Cut the Power Source",
          description: "Turn off the power at the circuit breaker or unplug the device. Use a non-conductive object like wood to separate the person from the source.",
          icon: "ToggleLeft",
          critical: true
        },
        {
          step: 3,
          title: "Call Emergency Services",
          description: "Call emergency services immediately, even if the person seems fine. Internal injuries from electrical shock may not be immediately visible.",
          icon: "Phone",
          critical: true
        },
        {
          step: 4,
          title: "Check for Response",
          description: "Once separated from the source, check if the person is breathing and has a pulse. Begin CPR if trained and necessary.",
          icon: "Heart",
          critical: true
        },
        {
          step: 5,
          title: "Treat Burns",
          description: "Cover any burns with sterile gauze. Do not apply ice, ointments, or remove burned clothing stuck to skin.",
          icon: "Bandage",
          critical: false
        }
      ]
    },
    outage: {
      title: "Power Outage",
      icon: "PowerOff",
      color: "var(--color-accent)",
      steps: [
        {
          step: 1,
          title: "Check the Scope",
          description: "Determine if the outage affects only your property or the entire neighborhood. Check with neighbors or your utility provider.",
          icon: "Search",
          critical: false
        },
        {
          step: 2,
          title: "Inspect Circuit Breakers",
          description: "Check your main circuit breaker and individual breakers. Look for tripped breakers (in the middle position) and reset if needed.",
          icon: "ToggleRight",
          critical: false
        },
        {
          step: 3,
          title: "Unplug Sensitive Electronics",
          description: "Unplug computers, TVs, and other sensitive electronics to protect them from power surges when electricity is restored.",
          icon: "Unplug",
          critical: false
        },
        {
          step: 4,
          title: "Use Flashlights Only",
          description: "Never use candles during a power outage due to fire risk. Use battery-powered flashlights or LED lanterns instead.",
          icon: "Flashlight",
          critical: true
        },
        {
          step: 5,
          title: "Contact Professional",
          description: "If breakers trip repeatedly or you suspect internal wiring issues, call Gert at 082 793 5785 for emergency diagnosis.",
          icon: "Phone",
          critical: false
        }
      ]
    },
    sparking: {
      title: "Sparking Outlet",
      icon: "Sparkles",
      color: "var(--color-error)",
      steps: [
        {
          step: 1,
          title: "Stop Using Immediately",
          description: "Do not plug anything into the sparking outlet. Unplug any connected devices if you can do so safely without touching the outlet.",
          icon: "AlertTriangle",
          critical: true
        },
        {
          step: 2,
          title: "Turn Off Circuit Breaker",
          description: "Locate the circuit breaker for that outlet and turn it off. Label it so no one accidentally turns it back on.",
          icon: "ToggleLeft",
          critical: true
        },
        {
          step: 3,
          title: "Check for Damage",
          description: "Look for visible signs of damage like burn marks, melted plastic, or discoloration around the outlet. Do not touch.",
          icon: "Eye",
          critical: false
        },
        {
          step: 4,
          title: "Keep Area Clear",
          description: "Keep the area around the outlet clear of flammable materials. Do not cover the outlet or attempt any repairs yourself.",
          icon: "ShieldAlert",
          critical: true
        },
        {
          step: 5,
          title: "Call Emergency Service",
          description: "Contact Gert immediately at 082 793 5785. Sparking outlets are a fire hazard and require urgent professional attention.",
          icon: "Phone",
          critical: true
        }
      ]
    }
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:0827935785';
  };

  const currentScenario = scenarios?.[activeScenario];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-4">
            Emergency Electrical Safety Procedures
          </h2>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Know what to do in electrical emergencies. Follow these step-by-step procedures to protect yourself and others while minimizing damage.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12">
          {Object.entries(scenarios)?.map(([key, scenario]) => (
            <button
              key={key}
              onClick={() => setActiveScenario(key)}
              className={`p-4 md:p-6 rounded-xl border-2 transition-all duration-300 ${
                activeScenario === key
                  ? 'border-primary bg-primary text-primary-foreground shadow-lg'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex flex-col items-center gap-2 md:gap-3">
                <Icon
                  name={scenario?.icon}
                  size={24}
                  color={activeScenario === key ? 'currentColor' : scenario?.color}
                />
                <span className="font-headline font-semibold text-sm md:text-base text-center">
                  {scenario?.title}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8 lg:p-10">
          <div className="flex items-center gap-4 mb-8">
            <div
              className="flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-xl"
              style={{ backgroundColor: `${currentScenario?.color}15` }}
            >
              <Icon name={currentScenario?.icon} size={32} color={currentScenario?.color} />
            </div>
            <div>
              <h3 className="font-headline font-bold text-xl md:text-2xl text-foreground">
                {currentScenario?.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Follow these steps in order for your safety
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {currentScenario?.steps?.map((step) => (
              <div
                key={step?.step}
                className={`flex gap-4 md:gap-6 p-4 md:p-6 rounded-xl ${
                  step?.critical ? 'bg-error/5 border-2 border-error/20' : 'bg-muted'
                }`}
              >
                <div className="flex-shrink-0">
                  <div
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full font-headline font-bold text-lg"
                    style={{
                      backgroundColor: step?.critical ? 'var(--color-error)' : currentScenario?.color,
                      color: 'white'
                    }}
                  >
                    {step?.step}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h4 className="font-headline font-semibold text-base md:text-lg text-foreground">
                      {step?.title}
                    </h4>
                    {step?.critical && (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-error text-error-foreground rounded-full text-xs font-accent">
                        <Icon name="AlertTriangle" size={12} />
                        Critical
                      </span>
                    )}
                  </div>
                  <p className="font-body text-sm md:text-base text-muted-foreground">
                    {step?.description}
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <div
                    className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg"
                    style={{ backgroundColor: `${currentScenario?.color}15` }}
                  >
                    <Icon name={step?.icon} size={20} color={currentScenario?.color} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="font-headline font-semibold text-base md:text-lg text-foreground mb-1">
                  Need Immediate Help?
                </p>
                <p className="font-body text-sm text-muted-foreground">
                  Available 24/7 for electrical emergencies
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                onClick={handleEmergencyCall}
                iconName="Phone"
                iconPosition="left"
                className="bg-error hover:bg-error/90 text-error-foreground emergency-cta"
              >
                Emergency: 082 793 5785
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-warning/10 border border-warning/20 rounded-xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Icon name="ShieldAlert" size={32} color="var(--color-warning)" />
            </div>
            <div>
              <h4 className="font-headline font-semibold text-lg md:text-xl text-foreground mb-3">
                General Safety Reminders
              </h4>
              <ul className="space-y-2">
                {[
                  "Never attempt electrical repairs without proper training and equipment",
                  "Always assume electrical wires are live unless confirmed otherwise",
                  "Keep a Class C fire extinguisher accessible in your home",
                  "Know the location of your main circuit breaker panel",
                  "Test GFCI outlets monthly and smoke detectors weekly",
                  "Keep emergency contact numbers easily accessible"
                ]?.map((reminder, index) => (
                  <li key={index} className="flex items-start gap-2 font-body text-sm text-foreground">
                    <Icon name="Check" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                    <span>{reminder}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmergencyProcedures;